import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface DriverManagementProps {
  domain: string;
}

interface Driver {
  id: string;
  name: string;
  employeeId: string;
  phoneNumber: string;
  email: string;
  licenseNumber: string;
  licenseExpiry: string;
  address: string;
  dateOfJoining: string;
  assignedRoute: string;
  assignedVehicle: string;
  experience: string;
  status: 'Active' | 'On Leave' | 'Inactive';
}

const DriverManagement: React.FC<DriverManagementProps> = ({ domain }) => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    employeeId: '',
    phoneNumber: '',
    email: '',
    licenseNumber: '',
    licenseExpiry: '',
    address: '',
    dateOfJoining: '',
    assignedRoute: '',
    assignedVehicle: '',
    experience: '',
    status: 'Active' as 'Active' | 'On Leave' | 'Inactive',
  });

  useEffect(() => {
    fetchDrivers();
  }, [domain]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const driversRef = collection(db, `school-users/${domain}/transport-drivers`);
      const snapshot = await getDocs(driversRef);
      const driverList: Driver[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Driver));
      setDrivers(driverList);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      alert('Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.employeeId || !formData.phoneNumber || !formData.licenseNumber) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const driverData = { ...formData };

      if (editingDriver) {
        const driverRef = doc(db, `school-users/${domain}/transport-drivers`, editingDriver.id);
        await updateDoc(driverRef, driverData);
        alert('Driver updated successfully!');
      } else {
        await addDoc(collection(db, `school-users/${domain}/transport-drivers`), driverData);
        alert('Driver added successfully!');
      }

      resetForm();
      fetchDrivers();
    } catch (error) {
      console.error('Error saving driver:', error);
      alert('Failed to save driver');
    }
  };

  const handleEdit = (driver: Driver) => {
    setEditingDriver(driver);
    setFormData({
      name: driver.name,
      employeeId: driver.employeeId,
      phoneNumber: driver.phoneNumber,
      email: driver.email,
      licenseNumber: driver.licenseNumber,
      licenseExpiry: driver.licenseExpiry,
      address: driver.address,
      dateOfJoining: driver.dateOfJoining,
      assignedRoute: driver.assignedRoute,
      assignedVehicle: driver.assignedVehicle,
      experience: driver.experience,
      status: driver.status,
    });
    setShowForm(true);
  };

  const handleDelete = async (driverId: string) => {
    if (!confirm('Are you sure you want to delete this driver?')) return;

    try {
      await deleteDoc(doc(db, `school-users/${domain}/transport-drivers`, driverId));
      alert('Driver deleted successfully!');
      fetchDrivers();
    } catch (error) {
      console.error('Error deleting driver:', error);
      alert('Failed to delete driver');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      employeeId: '',
      phoneNumber: '',
      email: '',
      licenseNumber: '',
      licenseExpiry: '',
      address: '',
      dateOfJoining: '',
      assignedRoute: '',
      assignedVehicle: '',
      experience: '',
      status: 'Active',
    });
    setEditingDriver(null);
    setShowForm(false);
  };

  const filteredDrivers = drivers.filter(driver =>
    driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    driver.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    driver.phoneNumber.includes(searchTerm) ||
    driver.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-success';
      case 'On Leave': return 'bg-warning';
      case 'Inactive': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <i className="bi bi-person-badge me-2"></i>Driver Management
        </h5>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          <i className="bi bi-plus-circle me-2"></i>
          {showForm ? 'Cancel' : 'Add Driver'}
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card mb-4 shadow-sm">
          <div className="card-body">
            <h6 className="card-title mb-3">
              {editingDriver ? 'Edit Driver' : 'Add New Driver'}
            </h6>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Ramesh Kumar"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Employee ID *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleInputChange}
                    placeholder="e.g., EMP-D001"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Phone Number *</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., +91 9876543210"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="e.g., driver@school.com"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">License Number *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleInputChange}
                    placeholder="e.g., DL-1234567890123"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">License Expiry *</label>
                  <input
                    type="date"
                    className="form-control"
                    name="licenseExpiry"
                    value={formData.licenseExpiry}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-12">
                  <label className="form-label">Address</label>
                  <textarea
                    className="form-control"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Enter complete address"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Date of Joining</label>
                  <input
                    type="date"
                    className="form-control"
                    name="dateOfJoining"
                    value={formData.dateOfJoining}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Experience (years)</label>
                  <input
                    type="text"
                    className="form-control"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    placeholder="e.g., 5 years"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Assigned Route</label>
                  <input
                    type="text"
                    className="form-control"
                    name="assignedRoute"
                    value={formData.assignedRoute}
                    onChange={handleInputChange}
                    placeholder="e.g., R-01"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Assigned Vehicle</label>
                  <input
                    type="text"
                    className="form-control"
                    name="assignedVehicle"
                    value={formData.assignedVehicle}
                    onChange={handleInputChange}
                    placeholder="e.g., DL-1234"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="Active">Active</option>
                    <option value="On Leave">On Leave</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <button type="submit" className="btn btn-primary me-2">
                  <i className="bi bi-check-circle me-2"></i>
                  {editingDriver ? 'Update Driver' : 'Add Driver'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  <i className="bi bi-x-circle me-2"></i>Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="card mb-3 shadow-sm">
        <div className="card-body">
          <div className="input-group">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, employee ID, phone, or license number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Drivers Grid */}
      <div className="row g-3">
        {loading ? (
          <div className="col-12 text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredDrivers.length === 0 ? (
          <div className="col-12 text-center py-5 text-muted">
            <i className="bi bi-person-badge fs-1 d-block mb-3"></i>
            <p>No drivers found</p>
          </div>
        ) : (
          filteredDrivers.map((driver) => (
            <div key={driver.id} className="col-md-6 col-lg-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h6 className="card-title mb-1">{driver.name}</h6>
                      <span className="badge bg-primary">{driver.employeeId}</span>
                    </div>
                    <span className={`badge ${getStatusBadgeClass(driver.status)}`}>
                      {driver.status}
                    </span>
                  </div>
                  <div className="mb-2">
                    <small className="text-muted d-block">
                      <i className="bi bi-telephone me-1"></i>
                      {driver.phoneNumber}
                    </small>
                    {driver.email && (
                      <small className="text-muted d-block">
                        <i className="bi bi-envelope me-1"></i>
                        {driver.email}
                      </small>
                    )}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted d-block">
                      <i className="bi bi-card-text me-1"></i>
                      <strong>License:</strong> {driver.licenseNumber}
                    </small>
                    <small className="text-muted d-block">
                      <i className="bi bi-calendar-event me-1"></i>
                      <strong>Expiry:</strong> {driver.licenseExpiry || 'N/A'}
                    </small>
                  </div>
                  {driver.experience && (
                    <small className="text-muted d-block mb-2">
                      <i className="bi bi-award me-1"></i>
                      <strong>Experience:</strong> {driver.experience}
                    </small>
                  )}
                  <div className="mb-3">
                    {driver.assignedRoute && (
                      <small className="text-muted d-block">
                        <i className="bi bi-map me-1"></i>
                        <strong>Route:</strong> {driver.assignedRoute}
                      </small>
                    )}
                    {driver.assignedVehicle && (
                      <small className="text-muted d-block">
                        <i className="bi bi-truck me-1"></i>
                        <strong>Vehicle:</strong> {driver.assignedVehicle}
                      </small>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-primary flex-fill"
                      onClick={() => handleEdit(driver)}
                    >
                      <i className="bi bi-pencil me-1"></i>Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(driver.id)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DriverManagement;
